import { useSyncExternalStore } from 'react';
import {
  MCGRAW_HILL_HANDBOOK_EXAM_ID,
  MCGRAW_HILL_TEXTBOOK_EXAM_IDS,
} from './mcgrawHill';

/** The launcher's verticals a member can open (ORA-1247). */
export type McGrawHillVerticalKey = 'medicine' | 'nursing' | 'pharmacy' | 'vet';

/** The exam menu's entries and groups, as ExamSelectors lists them. */
export type McGrawHillExamMenuEntry =
  'preclinical' | 'step' | 'shelf' | 'comlex' | 'residency' | 'nclex';

interface McGrawHillVerticalScope {
  /** What the exam menu lists; none shows a one-line empty state. */
  examMenu: readonly McGrawHillExamMenuEntry[];
  /** The menu is NCLEX-RN, then a Courses group (DUT's course, ORA-1268). */
  hasCoursesMenu?: boolean;
  /** The block picker's McGraw Hill exams, in order. */
  demoExamIds: readonly string[];
  /** The Textbooks catalog's one category (`/mh/<slug>`). */
  catalogCategory: string;
  /** How the launcher makes the vertical's first exam active. */
  firstExam: 'step1' | 'katzung' | 'nursing' | 'none';
}

/**
 * mcgraw-hill (ORA-1247): what each vertical shows. A member picks one on the
 * launcher; the exam menu, the block picker, the NCLEX-RN nav item and the
 * catalog show only that vertical's content.
 */
export const MCGRAW_HILL_VERTICAL_SCOPES: Record<
  McGrawHillVerticalKey,
  McGrawHillVerticalScope
> = {
  medicine: {
    examMenu: ['preclinical', 'step', 'shelf', 'comlex', 'residency'],
    demoExamIds: MCGRAW_HILL_TEXTBOOK_EXAM_IDS,
    catalogCategory: 'medicine',
    firstExam: 'step1',
  },
  nursing: {
    // NCLEX-RN, then the Courses group (DutExamMenu).
    examMenu: ['nclex'],
    hasCoursesMenu: true,
    demoExamIds: [MCGRAW_HILL_HANDBOOK_EXAM_ID],
    catalogCategory: 'nursing',
    firstExam: 'nursing',
  },
  pharmacy: {
    examMenu: ['preclinical'],
    demoExamIds: MCGRAW_HILL_TEXTBOOK_EXAM_IDS,
    catalogCategory: 'pharmacy',
    firstExam: 'katzung',
  },
  vet: {
    examMenu: [],
    demoExamIds: [],
    catalogCategory: 'veterinary',
    firstExam: 'none',
  },
};

export const isMcGrawHillVerticalKey = (
  value: unknown
): value is McGrawHillVerticalKey =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(MCGRAW_HILL_VERTICAL_SCOPES, value);

const STORAGE_KEY = 'mcgraw-hill-vertical';
const DEFAULT_VERTICAL: McGrawHillVerticalKey = 'medicine';

// The vertical is a per-browser choice; storage can be unavailable.
const readStoredVertical = (): McGrawHillVerticalKey => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isMcGrawHillVerticalKey(stored) ? stored : DEFAULT_VERTICAL;
  } catch {
    return DEFAULT_VERTICAL;
  }
};

// Read from storage on first use, then kept here.
let current: McGrawHillVerticalKey | null = null;
const listeners = new Set<() => void>();

/** The picked vertical. */
export const getMcGrawHillVertical = (): McGrawHillVerticalKey => {
  current ??= readStoredVertical();
  return current;
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setMcGrawHillVertical = (next: McGrawHillVerticalKey): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Not remembered past this tab; the vertical still applies.
  }

  if (current === next) return;
  current = next;
  listeners.forEach((listener) => listener());
};

/** The member's vertical (Medicine until they pick one). */
export const useMcGrawHillVertical = (): McGrawHillVerticalKey =>
  useSyncExternalStore(subscribe, getMcGrawHillVertical, getMcGrawHillVertical);

/** The member's vertical's scope. */
export const useMcGrawHillVerticalScope = (): McGrawHillVerticalScope =>
  MCGRAW_HILL_VERTICAL_SCOPES[useMcGrawHillVertical()];

/** Forgets the in-memory vertical so the next read goes to storage (tests). */
export const resetMcGrawHillVertical = (): void => {
  current = null;
};
